/**
 * Dentis Cart Sync Manager (NL)
 * Keeps the cart drawer in sync and manages D2 Pro included items.
 *
 * Strategy:
 * - Debounce cart changes so grouped activity settles first
 * - Statically show the included section immediately in Liquid
 * - Add/update the real included items in the cart via AJAX
 * - Hide those managed included items from the visible cart UI
 *
 * Checkout:
 * - The managed included items use dedicated $0 product variants
 * - They are inherently free at checkout (no discount logic needed)
 */

(function() {
  'use strict';

  const DEBOUNCE_MS = 100;
  const INIT_BLOCK_MS = 2000;
  const DEBUG = true;
  const SYNC_HEADER_NAME = 'X-Dentis-Included-Sync';
  const INCLUDED_FLAG_KEY = '_dentis_included';
  const INCLUDED_SOURCE_KEY = '_dentis_included_source';
  const INCLUDED_ROLE_KEY = '_dentis_included_role';

  // D2 Pro Toothbrush Variant IDs (main products that qualify for free items)
  const D2_PRO_VARIANT_IDS = [
    46499264430220, // Zwart / Startpakket
    46499264462988, // Wit / Startpakket
    46499264495756  // Zwart & Wit / Duopakket
  ];

  // $0 Brush Head Variant IDs (dedicated free products)
  const BRUSHHEAD_VARIANT_IDS = [
    46881941651596, // Zwart  ($0 product: 4-stuks-d2-pro-borsteltjes-free-1)
    46881941127308, // Wit    ($0 product: 4-stuks-d2-pro-borsteltjes-free)
    46881941258380  // Duo    ($0 product: 8-stuks-d2-pro-borsteltjes-free)
  ];

  // $0 Toothpaste Variant ID (dedicated free product)
  const TOOTHPASTE_VARIANT_ID = 46881940504716;
  const INCLUDED_SOURCE_VALUE = 'd2pro';

  function log(...args) {
    if (DEBUG) console.log('[DentisCartSync]', ...args);
  }

  let debounceTimer = null;
  let isRefreshing = false;
  let isInitialized = false;
  let isRemovingItems = false;
  let isProcessing = false;
  let hasQueuedRun = false;
  let lastRemovalTime = 0;
  const REMOVAL_COOLDOWN_MS = 1500;
  let cartCrosssellInteractionsBound = false;
  let cartCrosssellDragScroller = null;
  let cartCrosssellDragPointerId = null;
  let cartCrosssellDragStartX = 0;
  let cartCrosssellDragStartScrollLeft = 0;
  let cartCrosssellHasDragged = false;
  let cartCrosssellLastDragAt = 0;

  function getRequestUrl(input) {
    if (typeof input === 'string') return input;
    if (input && typeof input.url === 'string') return input.url;
    return '';
  }

  function getHeaderValue(headers, name) {
    if (!headers) return null;

    if (typeof Headers !== 'undefined' && headers instanceof Headers) {
      return headers.get(name);
    }

    if (Array.isArray(headers)) {
      const match = headers.find((entry) => entry[0] && entry[0].toLowerCase() === name.toLowerCase());
      return match ? match[1] : null;
    }

    if (typeof headers === 'object') {
      const key = Object.keys(headers).find((headerKey) => headerKey.toLowerCase() === name.toLowerCase());
      return key ? headers[key] : null;
    }

    return null;
  }

  function hasSyncHeader(input, init) {
    const initHeader = getHeaderValue(init && init.headers, SYNC_HEADER_NAME);
    if (initHeader) return true;

    if (input && typeof input === 'object') {
      const inputHeader = getHeaderValue(input.headers, SYNC_HEADER_NAME);
      return Boolean(inputHeader);
    }

    return false;
  }

  function isObservedCartRequest(url) {
    return /\/cart\/(add|change|update)(\.js)?(?:\?|$)/.test(url);
  }

  function observeCartRequests() {
    if (!window.fetch) return;

    const originalFetch = window.fetch;
    window.fetch = function(input, init) {
      const url = getRequestUrl(input);
      const shouldObserve = isObservedCartRequest(url) && !hasSyncHeader(input, init);
      const fetchPromise = originalFetch.apply(this, arguments);

      if (shouldObserve) {
        fetchPromise
          .then((response) => {
            if (response && response.ok) {
              notifyCartChange();
            }
          })
          .catch(() => {});
      }

      return fetchPromise;
    };
  }

  function getItemProperty(item, key) {
    if (!item || !item.properties) return '';
    const value = item.properties[key];
    return value == null ? '' : String(value);
  }

  function isManagedIncludedItem(item) {
    return (
      getItemProperty(item, INCLUDED_FLAG_KEY) === 'true' &&
      getItemProperty(item, INCLUDED_SOURCE_KEY) === INCLUDED_SOURCE_VALUE
    );
  }

  function buildIncludedProperties(role) {
    return {
      [INCLUDED_FLAG_KEY]: 'true',
      [INCLUDED_SOURCE_KEY]: INCLUDED_SOURCE_VALUE,
      [INCLUDED_ROLE_KEY]: role
    };
  }

  function getD2ProQuantities(cart) {
    const quantities = {
      black: 0,
      white: 0,
      duo: 0,
      total: 0
    };

    if (!cart || !Array.isArray(cart.items)) {
      return quantities;
    }

    cart.items.forEach((item) => {
      if (item.variant_id === 46499264430220) {
        quantities.black += item.quantity;
      } else if (item.variant_id === 46499264462988) {
        quantities.white += item.quantity;
      } else if (item.variant_id === 46499264495756) {
        quantities.duo += item.quantity;
      }
    });

    quantities.total = quantities.black + quantities.white + quantities.duo;
    return quantities;
  }

  function buildDesiredIncludedLines(quantities) {
    const desired = [];

    if (quantities.black > 0) {
      desired.push({
        role: 'toothpaste-black',
        variantId: TOOTHPASTE_VARIANT_ID,
        quantity: quantities.black,
        properties: buildIncludedProperties('toothpaste-black')
      });
      desired.push({
        role: 'brushheads-black',
        variantId: 46881941651596,
        quantity: quantities.black,
        properties: buildIncludedProperties('brushheads-black')
      });
    }

    if (quantities.white > 0) {
      desired.push({
        role: 'toothpaste-white',
        variantId: TOOTHPASTE_VARIANT_ID,
        quantity: quantities.white,
        properties: buildIncludedProperties('toothpaste-white')
      });
      desired.push({
        role: 'brushheads-white',
        variantId: 46881941127308,
        quantity: quantities.white,
        properties: buildIncludedProperties('brushheads-white')
      });
    }

    if (quantities.duo > 0) {
      desired.push({
        role: 'toothpaste-duo',
        variantId: TOOTHPASTE_VARIANT_ID,
        quantity: quantities.duo * 2,
        properties: buildIncludedProperties('toothpaste-duo')
      });
      desired.push({
        role: 'brushheads-duo',
        variantId: 46881941258380,
        quantity: quantities.duo,
        properties: buildIncludedProperties('brushheads-duo')
      });
    }

    return desired;
  }

  async function fetchCart() {
    const response = await fetch('/cart.js');
    return response.json();
  }

  async function syncIncludedItems() {
    const cart = await fetchCart();
    const quantities = getD2ProQuantities(cart);
    const desiredLines = buildDesiredIncludedLines(quantities);
    const desiredByRole = new Map(desiredLines.map((line) => [line.role, line]));
    const currentIncludedLines = cart.items.filter((item) => isManagedIncludedItem(item));
    const currentByRole = new Map();
    const updates = {};
    const adds = [];

    currentIncludedLines.forEach((item) => {
      const role = getItemProperty(item, INCLUDED_ROLE_KEY);
      if (!role) {
        updates[item.key] = 0;
        return;
      }

      if (currentByRole.has(role)) {
        updates[item.key] = 0;
        return;
      }

      currentByRole.set(role, item);
    });

    currentByRole.forEach((item, role) => {
      const desired = desiredByRole.get(role);

      if (!desired) {
        updates[item.key] = 0;
        return;
      }

      if (item.variant_id !== desired.variantId) {
        updates[item.key] = 0;
        adds.push({
          id: desired.variantId,
          quantity: desired.quantity,
          properties: desired.properties
        });
        desiredByRole.delete(role);
        return;
      }

      if (item.quantity !== desired.quantity) {
        updates[item.key] = desired.quantity;
      }

      desiredByRole.delete(role);
    });

    desiredByRole.forEach((desired) => {
      adds.push({
        id: desired.variantId,
        quantity: desired.quantity,
        properties: desired.properties
      });
    });

    const hasUpdates = Object.keys(updates).length > 0;
    const hasAdds = adds.length > 0;

    if (!hasUpdates && !hasAdds) {
      log('Included items already match D2 Pro quantities');
      return false;
    }

    log('Syncing managed included items', { updates, adds });

    if (hasUpdates) {
      const updateResponse = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          [SYNC_HEADER_NAME]: '1'
        },
        body: JSON.stringify({ updates })
      });

      if (!updateResponse.ok) {
        throw new Error('Failed to update managed included items');
      }
    }

    if (hasAdds) {
      const addResponse = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          [SYNC_HEADER_NAME]: '1'
        },
        body: JSON.stringify({ items: adds })
      });

      if (!addResponse.ok) {
        throw new Error('Failed to add managed included items');
      }
    }

    return true;
  }

  function notifyCartChange() {
    log('notifyCartChange() - initialized:', isInitialized);

    if (!isInitialized) {
      log('Blocked - still in init period');
      return;
    }

    if (debounceTimer) {
      clearTimeout(debounceTimer);
    }

    debounceTimer = setTimeout(() => {
      log('Activity settled, processing cart state...');
      processCartState();
    }, DEBOUNCE_MS);
  }

  const ALL_INCLUDED_VARIANT_IDS = [
    TOOTHPASTE_VARIANT_ID,
    ...BRUSHHEAD_VARIANT_IDS
  ];

  async function checkAndRemoveOrphanedIncludedItems() {
    if (isRemovingItems) {
      log('Already removing items, skipping check');
      return false;
    }

    const timeSinceRemoval = Date.now() - lastRemovalTime;
    if (lastRemovalTime > 0 && timeSinceRemoval < REMOVAL_COOLDOWN_MS) {
      log('In removal cooldown, skipping check');
      return false;
    }

    try {
      log('Checking for orphaned included items...');
      const cart = await fetchCart();

      if (!cart.items || cart.items.length === 0) {
        return false;
      }

      const hasD2Pro = cart.items.some(item => D2_PRO_VARIANT_IDS.includes(item.variant_id));

      if (hasD2Pro) {
        log('D2 Pro present, included items are valid');
        return false;
      }

      const orphanedItems = cart.items.filter(item =>
        ALL_INCLUDED_VARIANT_IDS.includes(item.variant_id) || isManagedIncludedItem(item)
      );

      if (orphanedItems.length === 0) {
        log('No orphaned included items found');
        return false;
      }

      log('Found', orphanedItems.length, 'orphaned included item(s), removing...');
      isRemovingItems = true;

      const updates = {};
      orphanedItems.forEach(item => {
        updates[item.key] = 0;
        log('  Marking for removal:', item.title, '(key:', item.key, ')');
      });

      const removeResponse = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          [SYNC_HEADER_NAME]: '1'
        },
        body: JSON.stringify({ updates })
      });

      if (!removeResponse.ok) {
        throw new Error('Failed to remove orphaned included items');
      }

      log('Orphaned included items removed');
      isRemovingItems = false;
      lastRemovalTime = Date.now();
      return true;

    } catch (error) {
      log('Error checking/removing orphaned items:', error);
      isRemovingItems = false;
      return false;
    }
  }

  async function processCartState() {
    if (isProcessing) {
      hasQueuedRun = true;
      return;
    }

    isProcessing = true;

    try {
      let didChange = false;

      try {
        didChange = await syncIncludedItems();
      } catch (error) {
        log('Error syncing included items:', error);
      }

      try {
        const didRemoveOrphans = await checkAndRemoveOrphanedIncludedItems();
        didChange = didChange || didRemoveOrphans;
      } catch (error) {
        log('Error checking orphaned items:', error);
      }

      if (didChange || document.querySelector('cart-drawer')) {
        refreshCart(true);
      }
    } finally {
      isProcessing = false;

      if (hasQueuedRun) {
        hasQueuedRun = false;
        notifyCartChange();
      }
    }
  }

  function getSectionInnerHTML(html, selector = '.shopify-section') {
    if (!html) return null;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const el = doc.querySelector(selector);
    return el ? el.innerHTML : null;
  }

  function getSectionsToRefresh() {
    const sections = [
      {
        key: 'cart-icon-bubble',
        targetId: 'cart-icon-bubble',
        selector: '.shopify-section'
      }
    ];

    if (document.getElementById('CartDrawer')) {
      sections.push({
        key: 'cart-drawer',
        targetId: 'CartDrawer',
        selector: '#CartDrawer'
      });
    }

    const mainCartItems = document.getElementById('main-cart-items');
    if (mainCartItems && mainCartItems.dataset.id) {
      sections.push({
        key: mainCartItems.dataset.id,
        targetId: 'main-cart-items',
        selector: '.js-contents'
      });
    }

    const mainCartFooter = document.getElementById('main-cart-footer');
    if (mainCartFooter && mainCartFooter.dataset.id) {
      sections.push({
        key: mainCartFooter.dataset.id,
        targetId: 'main-cart-footer',
        selector: '.js-contents'
      });
    }

    const cartLiveRegion = document.getElementById('cart-live-region-text');
    if (cartLiveRegion) {
      sections.push({
        key: 'cart-live-region-text',
        targetId: 'cart-live-region-text',
        selector: '.shopify-section'
      });
    }

    return sections;
  }

  function refreshRebuyCartCrosssellWidget(force = false) {
    const widgetMount = document.querySelector('#dentis-cart-crosssell-slider [data-rebuy-id]');
    if (!widgetMount) return;

    const widgetId = (widgetMount.getAttribute('data-rebuy-id') || '').trim();
    if (!widgetId) return;

    const hasHydratedContent = !!widgetMount.querySelector('.rebuy-widget');
    if (hasHydratedContent && !force) {
      bindCartCrosssellCardListeners();
      return;
    }

    if (!window.Rebuy) return;

    try {
      const widgets = Array.isArray(window.Rebuy.widgets) ? window.Rebuy.widgets : [];
      const widget = widgets.find((entry) => String(entry.id) === widgetId);

      if (widget) {
        if (typeof widget.getWidgetProducts === 'function') {
          widget.getWidgetProducts(() => {
            if (typeof widget.render === 'function') {
              widget.render();
            }
            setTimeout(bindCartCrosssellCardListeners, 300);
          });
          return;
        }

        if (typeof widget.render === 'function') {
          widget.render();
          setTimeout(bindCartCrosssellCardListeners, 300);
          return;
        }
      }

      if (typeof window.Rebuy.init === 'function') {
        window.Rebuy.init();
      }
    } catch (error) {
      log('Rebuy cart cross-sell refresh failed:', error);
    }

    setTimeout(bindCartCrosssellCardListeners, 500);
  }

  function getCartCrosssellWidgetContext(container) {
    const root = container || document;
    const widgetMount = root.querySelector('#dentis-cart-crosssell-slider [data-rebuy-id]');
    if (!widgetMount) return null;

    const widgetId = (widgetMount.getAttribute('data-rebuy-id') || '').trim();
    if (!widgetId || !window.Rebuy) return null;

    const widgets = Array.isArray(window.Rebuy.widgets) ? window.Rebuy.widgets : [];
    let widget = widgets.find((entry) => String(entry.id) === widgetId);

    if (!widget && typeof window.Rebuy.init === 'function') {
      try {
        window.Rebuy.init();
      } catch (error) {
        log('Rebuy init during cross-sell click failed:', error);
      }
      const refreshedWidgets = Array.isArray(window.Rebuy.widgets) ? window.Rebuy.widgets : [];
      widget = refreshedWidgets.find((entry) => String(entry.id) === widgetId);
    }

    if (!widget) return null;

    return { widget, widgetId };
  }

  function getWidgetProductByCard(widget, card) {
    if (!widget || !card) return null;
    const products = Array.isArray(widget.products)
      ? widget.products
      : (widget.data && Array.isArray(widget.data.products) ? widget.data.products : []);
    if (!products.length) return null;

    const index = parseInt(card.getAttribute('data-product-index') || '', 10);
    if (Number.isInteger(index) && index >= 0 && products[index]) {
      return products[index];
    }

    const productId = parseInt(card.getAttribute('data-shopify-product-id') || '', 10);
    if (Number.isInteger(productId) && productId > 0) {
      const byId = products.find((entry) => parseInt(entry && entry.id, 10) === productId);
      if (byId) return byId;
    }

    return null;
  }

  function addCartCrosssellProduct(widget, product, card) {
    if (!widget || !product || !card) return;
    if (card.dataset.crosssellAdding === 'true') return;

    card.dataset.crosssellAdding = 'true';
    card.classList.add('is-adding');

    let settled = false;
    const settle = () => {
      if (settled) return;
      settled = true;
      card.dataset.crosssellAdding = 'false';
      card.classList.remove('is-adding');
    };

    if (typeof widget.addSelectedProductsToCart === 'function') {
      try {
        if (typeof widget.productIsSelected === 'function') {
          if (!widget.productIsSelected(product) && typeof widget.selectProduct === 'function') {
            widget.selectProduct(product);
          }
        } else if (typeof widget.selectProduct === 'function') {
          widget.selectProduct(product);
        } else {
          product.selected = true;
        }
      } catch (error) {
        log('Could not preselect cross-sell product:', error);
      }

      widget.addSelectedProductsToCart({}, () => {
        settle();
        notifyCartChange();

        try {
          if (typeof widget.unselectProduct === 'function') {
            widget.unselectProduct(product);
          }
        } catch (error) {}
      });

      setTimeout(() => {
        try {
          if (typeof widget.unselectProduct === 'function') {
            widget.unselectProduct(product);
          }
        } catch (error) {}
        settle();
      }, 1800);
      return;
    }

    if (typeof widget.addToCart === 'function') {
      widget.addToCart(product, () => {
        settle();
        notifyCartChange();
      });
      setTimeout(settle, 1600);
      return;
    }

    const selectedVariantId = product.selected_variant && product.selected_variant.id
      ? product.selected_variant.id
      : (
        Array.isArray(product.variants) &&
        product.variants[0] &&
        product.variants[0].id
      );

    if (!selectedVariantId) {
      settle();
      return;
    }

    fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({
        items: [{ id: selectedVariantId, quantity: 1 }]
      })
    })
      .then(() => {
        notifyCartChange();
      })
      .catch(() => {})
      .finally(settle);
  }

  function extractVariantIdFromCard(card) {
    if (!card) return null;

    try {
      const vueNode = card.__vue__ || card.__vue_app__;
      if (vueNode) {
        const ctx = vueNode.$data || vueNode.ctx || vueNode;
        const product = ctx.product || ctx.$data && ctx.$data.product;
        if (product) {
          if (product.selected_variant && product.selected_variant.id) return product.selected_variant.id;
          if (Array.isArray(product.variants) && product.variants[0]) return product.variants[0].id;
        }
      }
    } catch (e) {}

    try {
      let el = card;
      while (el && el !== document.body) {
        const vue = el.__vue__;
        if (vue && vue.$data && Array.isArray(vue.$data.products || vue.products)) {
          const products = vue.$data.products || vue.products;
          const productId = parseInt(card.getAttribute('data-shopify-product-id') || '', 10);
          const index = parseInt(card.getAttribute('data-product-index') || '', 10);

          let product = null;
          if (Number.isInteger(index) && index >= 0 && products[index]) {
            product = products[index];
          } else if (Number.isInteger(productId) && productId > 0) {
            product = products.find(function(p) { return parseInt(p.id, 10) === productId; });
          }

          if (product) {
            if (product.selected_variant && product.selected_variant.id) return product.selected_variant.id;
            if (Array.isArray(product.variants) && product.variants[0]) return product.variants[0].id;
          }
          break;
        }
        el = el.parentElement;
      }
    } catch (e) {}

    const toggle = card.querySelector('.rebuy-addon-toggle');
    if (toggle && toggle.getAttribute('data-variant-id')) {
      const parsed = parseInt(toggle.getAttribute('data-variant-id'), 10);
      if (Number.isInteger(parsed) && parsed > 0) return parsed;
    }

    try {
      const context = getCartCrosssellWidgetContext(card.closest('#dentis-cart-crosssell'));
      if (context) {
        const product = getWidgetProductByCard(context.widget, card);
        if (product) {
          if (product.selected_variant && product.selected_variant.id) return product.selected_variant.id;
          if (Array.isArray(product.variants) && product.variants[0] && product.variants[0].id) {
            return product.variants[0].id;
          }
        }
      }
    } catch (e) {}

    return null;
  }

  function directAddToCart(variantId, card) {
    if (!variantId) return;
    if (card && card.dataset.crosssellAdding === 'true') return;

    if (card) {
      card.dataset.crosssellAdding = 'true';
      card.classList.add('is-adding');
    }

    let settled = false;
    const settle = () => {
      if (settled) return;
      settled = true;
      if (card) {
        card.dataset.crosssellAdding = 'false';
        card.classList.remove('is-adding');
      }
    };

    log('Cart cross-sell: adding variant', variantId);

    fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({
        items: [{ id: variantId, quantity: 1 }]
      })
    })
      .then((response) => {
        if (!response.ok) {
          log('Cart cross-sell add failed:', response.status);
        } else {
          log('Cart cross-sell add succeeded');
        }
        notifyCartChange();
      })
      .catch((error) => {
        log('Cart cross-sell add error:', error);
      })
      .finally(settle);
  }

  function findCrosssellCard(el) {
    if (!el || typeof el.closest !== 'function') return null;

    const crosssellRoot = el.closest('#dentis-cart-crosssell');
    if (!crosssellRoot) return null;

    const cardSelectors = [
      '.rebuy-addon-card',
      '.rebuy-product-block',
      '.rebuy-product-card',
      '[data-shopify-product-id]'
    ];

    for (let i = 0; i < cardSelectors.length; i++) {
      const match = el.closest(cardSelectors[i]);
      if (match && crosssellRoot.contains(match)) return match;
    }

    for (let i = 0; i < cardSelectors.length; i++) {
      const child = el.querySelector(cardSelectors[i]);
      if (child) return child;
    }

    return null;
  }

  function extractProductHandle(card) {
    if (!card) return null;

    const link = card.querySelector('a[href*="/products/"]') || card.closest('a[href*="/products/"]');
    if (link) {
      const match = link.getAttribute('href').match(/\/products\/([^?#/]+)/);
      if (match) return match[1];
    }

    return null;
  }

  function getVariantIdFromCardOrProduct(card) {
    if (!card) return Promise.resolve(null);

    const variantFromContext = extractVariantIdFromCard(card);
    if (variantFromContext) return Promise.resolve(variantFromContext);

    const shopifyProductId = card.getAttribute('data-shopify-product-id');
    const handle = extractProductHandle(card);

    if (handle) {
      return fetch('/products/' + handle + '.js')
        .then(function(r) { return r.json(); })
        .then(function(product) {
          if (!product || !Array.isArray(product.variants)) return null;
          var v = product.variants.find(function(entry) { return entry.available; }) || product.variants[0];
          return v && v.id ? v.id : null;
        })
        .catch(function() { return null; });
    }

    if (shopifyProductId) {
      return fetch('/products.json?product_ids=' + shopifyProductId)
        .then(function(r) { return r.json(); })
        .then(function(data) {
          var products = data && data.products;
          if (!Array.isArray(products) || !products.length) return null;
          var variants = products[0].variants;
          if (!Array.isArray(variants) || !variants.length) return null;
          var v = variants.find(function(entry) { return entry.available; }) || variants[0];
          return v && v.id ? v.id : null;
        })
        .catch(function() { return null; });
    }

    return Promise.resolve(null);
  }

  function handleCartCrosssellClick(event) {
    if (!event.target || typeof event.target.closest !== 'function') return;

    var insideCrosssell = event.target.closest('#dentis-cart-crosssell');
    if (!insideCrosssell) return;

    var card = findCrosssellCard(event.target);

    if (!card) return;
    if (Date.now() - cartCrosssellLastDragAt < 250) {
      log('Skipping click after recent drag');
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    if (card.dataset.crosssellAdding === 'true') {
      log('Cross-sell item is already adding');
      return;
    }

    getVariantIdFromCardOrProduct(card).then(function(variantId) {
      if (variantId) {
        directAddToCart(variantId, card);
      } else {
        log('Could not resolve variant ID for cross-sell card');
      }
    });
  }

  function handleCartCrosssellWheel(event) {
    const scroller = event.target && event.target.closest
      ? event.target.closest('#dentis-cart-crosssell .dentis-addon-options')
      : null;
    if (!scroller) return;

    if (Math.abs(event.deltaY) <= Math.abs(event.deltaX)) return;

    scroller.scrollLeft += event.deltaY;
    event.preventDefault();
  }

  function handleCartCrosssellPointerDown(event) {
    if (event.pointerType === 'touch') return;

    const scroller = event.target && event.target.closest
      ? event.target.closest('#dentis-cart-crosssell .dentis-addon-options')
      : null;
    if (!scroller) return;

    cartCrosssellDragScroller = scroller;
    cartCrosssellDragPointerId = event.pointerId;
    cartCrosssellDragStartX = event.clientX;
    cartCrosssellDragStartScrollLeft = scroller.scrollLeft;
    cartCrosssellHasDragged = false;
    scroller.classList.add('is-dragging');

    if (typeof scroller.setPointerCapture === 'function') {
      scroller.setPointerCapture(event.pointerId);
    }
  }

  function handleCartCrosssellPointerMove(event) {
    if (!cartCrosssellDragScroller || event.pointerId !== cartCrosssellDragPointerId) return;

    const deltaX = event.clientX - cartCrosssellDragStartX;
    if (Math.abs(deltaX) > 4) {
      cartCrosssellHasDragged = true;
    }

    cartCrosssellDragScroller.scrollLeft = cartCrosssellDragStartScrollLeft - deltaX;

    if (cartCrosssellHasDragged) {
      event.preventDefault();
    }
  }

  function releaseCartCrosssellPointerDrag(event) {
    if (!cartCrosssellDragScroller || event.pointerId !== cartCrosssellDragPointerId) return;

    if (cartCrosssellHasDragged) {
      cartCrosssellLastDragAt = Date.now();
    }

    if (typeof cartCrosssellDragScroller.releasePointerCapture === 'function') {
      cartCrosssellDragScroller.releasePointerCapture(event.pointerId);
    }

    cartCrosssellDragScroller.classList.remove('is-dragging');
    cartCrosssellDragScroller = null;
    cartCrosssellDragPointerId = null;
    cartCrosssellHasDragged = false;
  }

  function bindCartCrosssellCardListeners() {
    const crosssell = document.getElementById('dentis-cart-crosssell');
    if (!crosssell) return;

    const cards = crosssell.querySelectorAll('.rebuy-addon-card, .rebuy-product-block, [data-shopify-product-id]');
    cards.forEach(function(card) {
      if (card.dataset.dentisClickBound === 'true') return;
      card.dataset.dentisClickBound = 'true';

      card.addEventListener('click', function(event) {
        event.stopPropagation();
        event.stopImmediatePropagation();
        event.preventDefault();

        if (Date.now() - cartCrosssellLastDragAt < 250) {
          log('Skipping click after recent drag');
          return;
        }

        if (card.dataset.crosssellAdding === 'true') {
          log('Cross-sell item is already adding');
          return;
        }

        var variantId = extractVariantIdFromCard(card);
        if (variantId) {
          directAddToCart(variantId, card);
          return;
        }

        getVariantIdFromCardOrProduct(card).then(function(resolved) {
          if (resolved) {
            directAddToCart(resolved, card);
          } else {
            log('Could not resolve variant ID for cross-sell card');
            card.dataset.crosssellAdding = 'false';
            card.classList.remove('is-adding');
          }
        });
      }, true);
    });
  }

  function bindCartCrosssellInteractions() {
    if (cartCrosssellInteractionsBound) return;
    cartCrosssellInteractionsBound = true;

    document.addEventListener('click', handleCartCrosssellClick, true);
    document.addEventListener('wheel', handleCartCrosssellWheel, { passive: false });
    document.addEventListener('pointerdown', handleCartCrosssellPointerDown, true);
    document.addEventListener('pointermove', handleCartCrosssellPointerMove, true);
    document.addEventListener('pointerup', releaseCartCrosssellPointerDrag, true);
    document.addEventListener('pointercancel', releaseCartCrosssellPointerDrag, true);
  }

  function refreshCart(skipOrphanCheck = false) {
    log('refreshCart() - isRefreshing:', isRefreshing, 'skipOrphanCheck:', skipOrphanCheck);

    if (isRefreshing) {
      log('Already refreshing, skipping');
      return;
    }

    isRefreshing = true;
    log('Fetching cart sections...');

    const sectionsToRefresh = getSectionsToRefresh();
    const sectionKeys = sectionsToRefresh.map((section) => section.key).join(',');
    const url = new URL(window.location.href);
    url.searchParams.set('sections', sectionKeys);

    Promise.all([
      fetch(url.toString(), { method: 'GET' }).then(response => response.json()),
      fetchCart()
    ])
      .then(async ([sections, cart]) => {
        log('Got sections, updating DOM');

        const previousCrosssell = document.querySelector('#CartDrawer #dentis-cart-crosssell');
        const previousCrosssellWidgetMount = previousCrosssell
          ? previousCrosssell.querySelector('[data-rebuy-id]')
          : null;
        const previousCrosssellWidgetId = previousCrosssellWidgetMount
          ? (previousCrosssellWidgetMount.getAttribute('data-rebuy-id') || '').trim()
          : '';
        const previousCrosssellIsHydrated = !!(
          previousCrosssellWidgetMount &&
          previousCrosssellWidgetMount.querySelector('.rebuy-widget')
        );

        sectionsToRefresh.forEach((sectionConfig) => {
          const targetRoot = document.getElementById(sectionConfig.targetId);
          const sectionHtml = getSectionInnerHTML(sections[sectionConfig.key], sectionConfig.selector);
          if (!targetRoot || sectionHtml === null) return;

          if (sectionConfig.selector === '.js-contents') {
            const targetContent = targetRoot.querySelector('.js-contents') || targetRoot;
            targetContent.innerHTML = sectionHtml;
            return;
          }

          targetRoot.innerHTML = sectionHtml;

          if (
            sectionConfig.key === 'cart-drawer' &&
            previousCrosssell &&
            previousCrosssellIsHydrated
          ) {
            const nextCrosssell = targetRoot.querySelector('#dentis-cart-crosssell');
            const nextWidgetMount = nextCrosssell ? nextCrosssell.querySelector('[data-rebuy-id]') : null;
            const nextWidgetId = nextWidgetMount
              ? (nextWidgetMount.getAttribute('data-rebuy-id') || '').trim()
              : '';

            if (
              nextCrosssell &&
              previousCrosssellWidgetId &&
              nextWidgetId &&
              previousCrosssellWidgetId === nextWidgetId
            ) {
              nextCrosssell.replaceWith(previousCrosssell);
            }
          }
        });

        const visibleItemCount = Array.isArray(cart.items)
          ? cart.items.reduce((count, item) => count + (isManagedIncludedItem(item) ? 0 : item.quantity), 0)
          : 0;
        const cartDrawerWrapper = document.querySelector('cart-drawer');
        const cartItemsWrapper = document.querySelector('cart-items');
        const mainCartFooter = document.getElementById('main-cart-footer');
        if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', visibleItemCount === 0);
        if (cartItemsWrapper) cartItemsWrapper.classList.toggle('is-empty', visibleItemCount === 0);
        if (mainCartFooter) mainCartFooter.classList.toggle('is-empty', visibleItemCount === 0);

        const cartDrawerEl = document.querySelector('cart-drawer');
        const overlay = cartDrawerEl ? cartDrawerEl.querySelector('#CartDrawer-Overlay') : null;
        if (cartDrawerEl && overlay && typeof cartDrawerEl.close === 'function') {
          overlay.addEventListener('click', cartDrawerEl.close.bind(cartDrawerEl));
        }

        document.dispatchEvent(new CustomEvent('dentis:cart-drawer:updated'));

        refreshRebuyCartCrosssellWidget();

        isRefreshing = false;
        log('Cart refresh complete');

        if (!skipOrphanCheck) {
          await checkAndRemoveOrphanedIncludedItems();
        }
      })
      .catch(error => {
        log('Error refreshing cart:', error);
        isRefreshing = false;
      });
  }

  function forceRefresh() {
    log('forceRefresh() called');
    if (debounceTimer) {
      clearTimeout(debounceTimer);
      debounceTimer = null;
    }
    refreshCart();
  }

  document.addEventListener('rebuy:cart.change', function() {
    log('rebuy:cart.change event received');
    notifyCartChange();
  });

  document.addEventListener('rebuy.loaded', function() {
    refreshRebuyCartCrosssellWidget(true);
    setTimeout(bindCartCrosssellCardListeners, 600);
  });

  document.addEventListener('rebuy.ready', function() {
    setTimeout(bindCartCrosssellCardListeners, 200);
  });

  function init() {
    log('Initializing, blocking events for', INIT_BLOCK_MS, 'ms');
    bindCartCrosssellInteractions();

    setTimeout(() => {
      isInitialized = true;
      log('Ready - now accepting cart change events');
      processCartState();
    }, INIT_BLOCK_MS);
  }

  observeCartRequests();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.DentisCartSync = {
    notifyCartChange: notifyCartChange,
    forceRefresh: forceRefresh,
    checkOrphanedItems: checkAndRemoveOrphanedIncludedItems,
    syncIncludedItems: syncIncludedItems
  };

  log('Script loaded');

})();
